/* =========================================================================
 * IBC 4.1 — FULFILMENT PICKER
 * -------------------------------------------------------------------------
 * Delivery or collection, chosen in the basket. Drives the picker on the
 * cart page and in the drawer from one controller, because both surfaces
 * render snippets/ibc-fulfilment.liquid.
 *
 * FIVE RULES THIS FILE OBEYS
 * --------------------------
 * 1. THE CART IS THE STATE. Not this file. Every choice is written to a cart
 *    attribute, and `snippets/ibc-fulfilment-state.liquid` reads those
 *    attributes back. The module-level `state` below is a CACHE of what was
 *    last written, kept so the two surfaces can be updated without a round
 *    trip — never a second source of truth.
 *
 * 2. NOTHING IS BOUND TO AN ELEMENT. `assets/ibc-cart.js` replaces the whole
 *    contents of `[data-ibc-cart-refresh]` after every basket mutation, so
 *    any reference held here would point at a detached node within seconds.
 *    Every handler is delegated from `document` and re-reads the DOM.
 *
 * 3. BOTH SURFACES MOVE TOGETHER. On /cart the page picker and the drawer
 *    picker are both in the document, showing the same basket. A change to
 *    one is applied to every root, so they cannot disagree.
 *
 * 4. DATES ARE ANCHORED AT MIDDAY. Every Date built here is constructed at
 *    12:00 local. A date built at midnight and then shifted across a clock
 *    change lands on the previous day, which is the classic way a "Saturday"
 *    booking becomes a Friday one twice a year.
 *
 * 5. FAIL VISIBLY, NOT SILENTLY. If a write fails, the choice is rolled back
 *    in the UI and the cart's own error slot is used. A picker that shows a
 *    selection the cart does not have is worse than one that shows none.
 *
 * THE SEAM FOR REAL AVAILABILITY
 * ------------------------------
 * `slotsFor()` and `dayState()` are the only two functions that decide what
 * is offerable. The Pickup Scheduler app already answers both server-side at
 * /apps/ibc-pickup/availability, and when that is wired in, those two
 * functions become a fetch and a cache and NOTHING ELSE IN THIS FILE MOVES.
 * That is why they take a date and return a verdict rather than reaching
 * into the DOM.
 *
 * Depends on: nothing. Same ES5 dialect as assets/ibc-cart.js — the theme
 * ships no transpiler and this file has to run wherever that one does.
 * ====================================================================== */
(function () {
  'use strict';

  /* RE-ENTRY GUARD. Both cart sections emit this file, so on /cart the
   * browser executes it twice — one fetch, two <script> tags, two runs. The
   * second run would register a second document-level click listener and
   * every day cell would be selected, then immediately re-selected. Same
   * idiom, and same reason, as assets/ibc-cart.js. */
  if (window.IbcFulfilment) return;

  var MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  /* "Sept", not "Sep". It is what the shop's existing pickup labels already
   * say, and a basket that spells the month two ways looks broken. */
  var MONTHS_SHORT = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'
  ];

  var DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  var DAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  var MSG = {
    failed: 'Sorry, that could not be saved. Please try again.',
    gate: 'Choose delivery or collection above to continue.'
  };

  /* ==================================================================== */
  /* SMALL UTILITIES                                                      */
  /* ==================================================================== */

  function toArray(list) {
    return Array.prototype.slice.call(list || []);
  }

  function $all(selector, scope) {
    return toArray((scope || document).querySelectorAll(selector));
  }

  function $one(selector, scope) {
    return (scope || document).querySelector(selector);
  }

  function closest(node, selector) {
    while (node && node.nodeType === 1) {
      if (node.matches ? node.matches(selector) : false) return node;
      node = node.parentNode;
    }
    return null;
  }

  function pad(n) {
    return (n < 10 ? '0' : '') + n;
  }

  /* ==================================================================== */
  /* DATES                                                                */
  /* ==================================================================== */

  /* 'YYYY-MM-DD' -> Date at 12:00 LOCAL. See rule 4 above. Returns null for
   * anything that is not exactly that shape, because a half-parsed date is
   * how a picker ends up offering the 1st of January 1970. */
  function parseISO(value) {
    if (typeof value !== 'string' || value.length !== 10) return null;
    var y = Number(value.slice(0, 4));
    var m = Number(value.slice(5, 7));
    var d = Number(value.slice(8, 10));
    if (!y || !m || !d || value.charAt(4) !== '-' || value.charAt(7) !== '-') return null;
    var date = new Date(y, m - 1, d, 12, 0, 0, 0);
    /* Rejects 31 February rather than silently rolling it into March. */
    if (date.getFullYear() !== y || date.getMonth() !== m - 1 || date.getDate() !== d) return null;
    return date;
  }

  function toISO(date) {
    return date.getFullYear() + '-' + pad(date.getMonth() + 1) + '-' + pad(date.getDate());
  }

  function addDays(date, n) {
    var next = new Date(date.getTime());
    next.setDate(next.getDate() + n);
    next.setHours(12, 0, 0, 0);
    return next;
  }

  /* THERE IS NO addWorkingDays ANY MORE, and its absence is the point.
   *
   * The delivery lead time is CALENDAR days: it is how long the shop needs to
   * make the order and hand it to the courier, and the courier being shut on
   * a Monday does not make a cake take longer to bake. So the lead is a plain
   * day count — see minDate — and the closed weekdays do their work once, in
   * dayState, by shading the days a courier will not deliver on.
   *
   * Counting the lead in delivering days charged every closed day twice. On a
   * Saturday with Sunday and Monday closed and two days' lead, it offered
   * Wednesday: the Sunday and Monday were skipped by the count AND unavailable
   * to land on. The shop can ship that Monday for a Tuesday arrival, so
   * Tuesday is the honest answer.
   *
   * app/lib/delivery.js earliestDeliveryDate() is the same rule, in one
   * function rather than two, because the admin preview has to promise exactly
   * what the basket will offer. Change one, change the other. */

  function ordinal(n) {
    var rem = n % 100;
    if (rem >= 11 && rem <= 13) return n + 'th';
    if (n % 10 === 1) return n + 'st';
    if (n % 10 === 2) return n + 'nd';
    if (n % 10 === 3) return n + 'rd';
    return n + 'th';
  }

  /* 'Sunday 20 September' — the shop's existing pickup spelling. */
  function labelLong(date) {
    return DAYS[date.getDay()] + ' ' + date.getDate() + ' ' + MONTHS[date.getMonth()];
  }

  /* 'Sat 20th Sept' — short enough to sit inside a renamed delivery rate at
   * checkout without wrapping, and inside the function's 40-character cap. */
  function labelShort(date) {
    return DAYS_SHORT[date.getDay()] + ' ' + ordinal(date.getDate()) + ' ' + MONTHS_SHORT[date.getMonth()];
  }

  /* 'Saturday 20 September 2026' — the accessible name of a day cell. The
   * year is included because the grid pages past a year boundary. */
  function labelCell(date) {
    return labelLong(date) + ' ' + date.getFullYear();
  }

  function minutesToClock(mins) {
    var h24 = Math.floor(mins / 60) % 24;
    var m = mins % 60;
    var h = h24 % 12;
    if (h === 0) h = 12;
    return { text: h + ':' + pad(m), suffix: h24 < 12 ? 'am' : 'pm', h24: h24, m: m };
  }

  function clockToMinutes(value) {
    var bits = String(value || '').split(':');
    var h = Number(bits[0]);
    var m = Number(bits[1]);
    if (isNaN(h) || isNaN(m)) return null;
    return h * 60 + m;
  }

  /* '+0100' -> '+01:00'. Shopify's `%z` has no colon; ISO 8601 wants one and
   * so does every parser that will later read the order. */
  function tzColon(raw) {
    var value = String(raw || '').trim();
    if (/^[+-]\d{4}$/.test(value)) return value.slice(0, 3) + ':' + value.slice(3);
    if (/^[+-]\d{2}:\d{2}$/.test(value)) return value;
    return '+00:00';
  }

  /* ==================================================================== */
  /* CONFIGURATION, READ OFF THE ROOT                                     */
  /* ==================================================================== */

  function numAttr(root, name, fallback) {
    var raw = Number(root.getAttribute(name));
    return isNaN(raw) ? fallback : raw;
  }

  function listAttr(root, name) {
    return String(root.getAttribute(name) || '')
      .split(',')
      .map(function (part) { return part.trim(); })
      .filter(function (part) { return part.length > 0; });
  }

  /* OPENING HOURS, ONE ENTRY PER WEEKDAY, Sunday first — the order
   * `Date.getDay()` returns, so the weekday indexes the array directly.
   *
   * 'start-end' for a day the shop is open, empty for a day it is shut. An
   * empty segment is a CLOSED DAY, not a missing one: position carries the
   * weekday, so a shut Monday cannot be dropped without shifting Tuesday
   * onto Monday's hours. */
  function hoursAttr(root) {
    var raw = String(root.getAttribute('data-hours') || '').split(',');
    var out = [];
    for (var i = 0; i < 7; i++) {
      var bits = String(raw[i] || '').split('-');
      var from = clockToMinutes(bits[0]);
      var to = clockToMinutes(bits[1]);
      out.push(from === null || to === null || to <= from ? null : { from: from, to: to });
    }
    return out;
  }

  function conf(root) {
    return {
      today: parseISO(root.getAttribute('data-today')) || new Date(),
      tz: tzColon(root.getAttribute('data-tz')),
      deliveryLead: numAttr(root, 'data-delivery-lead', 2),
      pickupLead: numAttr(root, 'data-pickup-lead', 1),
      window: numAttr(root, 'data-window', 60),
      deliveryWindow: numAttr(root, 'data-delivery-window', 60),
      pickupClosedDays: listAttr(root, 'data-pickup-closed-days').map(Number),
      deliveryClosedDays: listAttr(root, 'data-delivery-closed-days').map(Number),
      closedDates: listAttr(root, 'data-closed-dates'),
      deliveryClosedDates: listAttr(root, 'data-delivery-closed-dates'),
      hours: hoursAttr(root),
      slot: numAttr(root, 'data-slot', 30),
      sameDay: root.getAttribute('data-same-day') === '1',
      cutoff: clockToMinutes(root.getAttribute('data-cutoff')),
      deliveryCutoff: clockToMinutes(root.getAttribute('data-delivery-cutoff')),
      slotCap: numAttr(root, 'data-slot-cap', 0),
      /* PREPARATION MINUTES THE BASKET NEEDS before it can be collected —
       * the largest custom.pickup_delay_minutes among the products in it,
       * floored at 60. Computed in Liquid because only the server can see
       * the line items' metafields. Whole cakes carry 180. */
      prepMinutes: numAttr(root, 'data-prep-minutes', 60),
      /* Whether each method is even rendered. Read so the controller can
       * stop looking for a tab that is not there rather than throwing. */
      pickupOffered: root.getAttribute('data-pickup-offered') !== '0',
      deliveryOffered: root.getAttribute('data-delivery-offered') !== '0',
      rulesSource: root.getAttribute('data-rules-source') || 'builtin',
      location: root.getAttribute('data-location') || ''
    };
  }

  function roots() {
    return $all('[data-ibc-fulfil]');
  }

  /* ==================================================================== */
  /* WHAT IS OFFERABLE — the seam. See the header note.                   */
  /* ==================================================================== */

  /* SAME-DAY COLLECTION IS DECIDED HERE, not in Liquid.
   *
   * It depends on the time of day, and Liquid's `now` is the moment the
   * section was rendered — a cached section would hand a customer a cutoff
   * that passed an hour ago. So the app's `same_day_pickup_enabled` and
   * `same_day_pickup_cutoff_time` arrive as data and the clock is read here.
   *
   * Caveat, and it is the browser's clock: a customer in another timezone
   * before their own local cutoff may be offered same-day collection they
   * cannot make. Bounded, and the alternative is a cutoff that lies to
   * everybody rather than to travellers. */
  function pickupLeadNow(cfg) {
    if (!cfg.sameDay || cfg.cutoff === null) return cfg.pickupLead;
    var now = new Date();
    return now.getHours() * 60 + now.getMinutes() < cfg.cutoff ? 0 : cfg.pickupLead;
  }

  /* THE DELIVERY CUT-OFF, for the same reason and with the same caveat as
   * the collection one: it is a time-of-day question, so it is decided
   * against the browser's clock rather than baked in by a section that may
   * have been cached an hour ago. Past the cut-off, today stops counting
   * towards the lead time. */
  function deliveryLeadNow(cfg) {
    if (cfg.deliveryCutoff === null) return cfg.deliveryLead;
    var now = new Date();
    var mins = now.getHours() * 60 + now.getMinutes();
    return mins >= cfg.deliveryCutoff ? cfg.deliveryLead + 1 : cfg.deliveryLead;
  }

  /* THE EARLIEST DATE WORTH OFFERING, before the calendar's own shading has a
   * say. For delivery that is today plus the lead in plain days; dayState then
   * refuses any closed weekday or blocked date at or after it, so the first
   * cell the customer can actually click is the first DELIVERABLE day on or
   * after this one. Two rules, each applied once. */
  function minDate(cfg, kind) {
    return kind === 'pickup'
      ? addDays(cfg.today, pickupLeadNow(cfg))
      : addDays(cfg.today, deliveryLeadNow(cfg));
  }

  function maxDate(cfg, kind) {
    return addDays(cfg.today, kind === 'pickup' ? cfg.window : cfg.deliveryWindow);
  }

  /* Returns 'ok' | 'early' | 'late' | 'closed'. Four answers rather than a
   * boolean, so the grid can style "not yet" differently from "we are shut"
   * and a screen reader can say which. */
  function dayState(cfg, kind, date) {
    var iso = toISO(date);
    if (date < minDate(cfg, kind)) return 'early';
    if (date > maxDate(cfg, kind)) return 'late';
    /* Two blackout lists, deliberately separate: the shop can be open when
     * the couriers are not, and a bank holiday is exactly that case. */
    var closedDates = kind === 'pickup' ? cfg.closedDates : cfg.deliveryClosedDates;
    if (closedDates.indexOf(iso) !== -1) return 'closed';
    var closedDays = kind === 'pickup' ? cfg.pickupClosedDays : cfg.deliveryClosedDays;
    if (closedDays.indexOf(date.getDay()) !== -1) return 'closed';

    /* For collection, a day with no hours has no slots, so offering it would
     * hand the customer an empty time grid. The hours table is the authority
     * and the closed-days list is a convenience that agrees with it. */
    if (kind === 'pickup' && !cfg.hours[date.getDay()]) return 'closed';

    /* AND A DAY WHOSE SLOTS HAVE ALL GONE PAST is 'early', not 'ok'.
     *
     * Preparation time makes this a live case rather than an edge one: a
     * basket with a whole cake in it needs three hours, so at 7pm on a day
     * the shop shuts at 9pm there is nothing left to book even though the
     * day is open and within the horizon. Reporting 'ok' here would light
     * the cell, let it be clicked, and then show an empty time grid.
     *
     * 'early' rather than 'closed' because that is what it is — too soon,
     * not shut — and the grid styles and announces the two differently. */
    if (kind === 'pickup' && slotsFor(cfg, date).length === 0) return 'early';

    return 'ok';
  }

  /* Every collection slot on a given day, as {start, end, time, label, from}.
   *
   * THE DAY'S OWN HOURS, not one pair for the week. The app's weekly_hours
   * gives every weekday its own start and end, and a Saturday that shuts at
   * 6pm should not be offered a 8:30pm slot because Friday is open late.
   *
   * The last slot is the last one that ENDS by closing time, which is why
   * the loop tests `start + slot <= to` rather than `start <= to`. Offering
   * a 20:45 slot on a 21:00 close would be promising a collection fifteen
   * minutes after the door is locked. */
  function slotsFor(cfg, date) {
    var out = [];
    var day = cfg.hours[date.getDay()];
    if (!day || cfg.slot <= 0) return out;
    var iso = toISO(date);

    /* THE PREPARATION FLOOR. Nothing may be offered sooner than
     * now + the basket's preparation minutes, which is the largest
     * custom.pickup_delay_minutes among its products with a 60-minute
     * floor. Three whole cakes do not take nine hours — the longest single
     * delay wins, and that decision is made in Liquid.
     *
     * This is also what stops a slot that has simply already happened being
     * offered: at 8pm, this morning's 10:00 is behind `earliest` like
     * everything else. Before this, same-day collection with a cut-off of
     * 21:00 offered a 10am slot at 8pm.
     *
     * The browser's clock again, and the same bounded caveat as the two
     * cut-offs: a customer whose device clock is wrong, or who is in
     * another timezone, gets an offer they may not be able to keep. The
     * alternative is a floor computed at render time, which a cached
     * section makes wrong for everybody. */
    var earliest = new Date(Date.now() + Math.max(0, cfg.prepMinutes) * 60000);

    for (var start = day.from; start + cfg.slot <= day.to; start += cfg.slot) {
      var end = start + cfg.slot;

      /* Built from the day's own midnight and the slot's minute offset, so
       * a slot pushed past midnight by the preparation time is compared on
       * the right day rather than wrapping. */
      var startsAt = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
      startsAt.setMinutes(start);
      if (startsAt.getTime() < earliest.getTime()) continue;

      var a = minutesToClock(start);
      var b = minutesToClock(end);
      /* One suffix when the slot stays inside a half of the day, which is
       * the shop's existing spelling ("8:00–8:30pm"); both when it straddles
       * noon or midnight, where one suffix would be a lie. */
      var time = a.suffix === b.suffix
        ? a.text + '–' + b.text + b.suffix
        : a.text + a.suffix + '–' + b.text + b.suffix;
      out.push({
        /* The start WITH its meridiem. Dropping it made the tiles narrower
         * but left a column reading "1:00", which a customer glancing at a
         * collection grid can read as one in the morning. Seven characters
         * is worth paying for that. */
        from: a.text + a.suffix,
        start: iso + 'T' + pad(a.h24) + ':' + pad(a.m) + ':00' + cfg.tz,
        end: iso + 'T' + pad(b.h24) + ':' + pad(b.m) + ':00' + cfg.tz,
        time: time,
        label: labelLong(date) + ', ' + time
      });
    }
    return out;
  }

  /* ==================================================================== */
  /* THE CACHE OF WHAT WAS LAST WRITTEN                                   */
  /* ==================================================================== */

  /* THE STAGE IS NOT CART DATA, and is deliberately not in `state`.
   *
   * Which step of the drawer's flow is on screen is a view, not a fact about
   * the order — it has no business being written to a cart attribute, and a
   * customer who reloads should land where their DATA puts them rather than
   * wherever they happened to have got to. So Liquid derives an opening
   * stage from the attributes, this variable tracks it while they walk, and
   * a section swap hands the derivation back to Liquid.
   *
   * 'all' is the cart page: every stage on screen at once, no walking. */
  var stage = 'closed';

  var STAGES = ['closed', 'options', 'calendar', 'slots', 'review', 'all'];

  var state = {
    method: '',
    option: '',
    deliveryDate: '',
    deliveryLabel: '',
    pickupDate: '',
    pickupStart: '',
    pickupEnd: '',
    pickupLabel: ''
  };

  /* THE LAST STATE THE CART IS KNOWN TO HAVE ACCEPTED.
   *
   * Not the same thing as `state`, which runs ahead of the network so the UI
   * can answer a click immediately. When a write fails, THIS is what the UI
   * is put back to.
   *
   * It cannot be recovered by re-reading the DOM: `syncRoot` stamps the
   * optimistic choice onto the root's own data attributes, so by the time a
   * rejection arrives the markup already agrees with the write that failed.
   * That is exactly the bug this variable exists to fix — the first version
   * rolled back by re-seeding from those attributes and therefore rolled
   * back to the thing it was trying to undo. */
  var committed = null;

  function snapshot() {
    var copy = {};
    Object.keys(state).forEach(function (key) { copy[key] = state[key]; });
    return copy;
  }

  function restore(saved) {
    if (!saved) return;
    Object.keys(saved).forEach(function (key) { state[key] = saved[key]; });
  }

  /* Seeded from the markup Liquid rendered, so a reload, a back button or a
   * section refresh all start from the cart rather than from zero. */
  function seed() {
    var root = roots()[0];
    if (!root) return;
    state.method = root.getAttribute('data-method') || '';
    state.option = root.getAttribute('data-option') || '';
    state.deliveryDate = root.getAttribute('data-delivery-date') || '';
    state.pickupDate = root.getAttribute('data-pickup-date') || '';
    state.pickupStart = root.getAttribute('data-pickup-start') || '';

    var d = parseISO(state.deliveryDate);
    state.deliveryLabel = d ? labelShort(d) : '';

    var p = parseISO(state.pickupDate);
    if (p && state.pickupStart) {
      var cfg = conf(root);
      var matches = slotsFor(cfg, p).filter(function (slot) {
        return slot.start === state.pickupStart;
      });
      if (matches.length) {
        state.pickupEnd = matches[0].end;
        state.pickupLabel = matches[0].label;
      }
    }

    /* Liquid just told us what the cart holds, so this IS the known-good
     * baseline — including after a section swap, where the server has had
     * the last word. */
    committed = snapshot();

    /* And it has also worked out which step that data belongs on. Trusting
     * its answer rather than keeping our own is what makes the flow survive
     * a quantity change mid-walk. */
    var fromLiquid = root.getAttribute('data-stage') || 'closed';
    stage = STAGES.indexOf(fromLiquid) === -1 ? 'closed' : fromLiquid;
  }

  function isFooter(root) {
    return root.getAttribute('data-layout') === 'footer';
  }

  /* Where "back" and "change" land, and where a fresh choice moves to. One
   * table rather than transitions scattered through the handlers, because
   * the flow is the thing most likely to be revised. */
  function nextStage(what) {
    if (stage === 'all') return 'all';
    if (what === 'method') return state.method === 'pickup' ? 'calendar' : 'options';
    if (what === 'option') return state.option === 'scheduled' ? 'calendar' : 'review';
    if (what === 'date') return state.method === 'pickup' ? 'slots' : 'review';
    if (what === 'slot') return 'review';
    return stage;
  }

  /* WHERE THIS CART BELONGS. The same answer Liquid works out on first
   * paint, needed again whenever the panel is reopened — by the close
   * control, or by tapping the tile that is already lit. Reopening must land
   * on the step the DATA implies, not on the first step of the flow, or a
   * finished basket sends its customer back to the beginning. */
  function resumeStage() {
    if (ready()) return 'review';
    if (state.method === 'pickup') return state.pickupDate ? 'slots' : 'calendar';
    if (state.method === 'delivery') return state.option === 'scheduled' ? 'calendar' : 'options';
    return 'options';
  }

  function setStage(next) {
    if (stage === 'all') return;
    if (STAGES.indexOf(next) === -1) return;
    stage = next;
    syncAll();
  }

  function ready() {
    if (state.method === 'pickup') return !!(state.pickupStart && state.pickupLabel);
    if (state.method === 'delivery') {
      if (state.option === 'standard') return true;
      if (state.option === 'scheduled') return !!(state.deliveryDate && state.deliveryLabel);
    }
    return false;
  }

  function summaryText(root) {
    if (state.method === 'pickup') {
      return state.pickupLabel ? 'Collect: ' + state.pickupLabel : '';
    }
    if (state.method === 'delivery') {
      if (state.option === 'standard') {
        var standard = $one('[data-ibc-fulfil-choice="standard"] .ibc-fulfil__choice-title', root);
        return standard ? standard.textContent.trim() : 'Standard delivery';
      }
      if (state.option === 'scheduled' && state.deliveryLabel) {
        return 'Delivery: ' + state.deliveryLabel;
      }
    }
    return '';
  }

  /* ==================================================================== */
  /* WRITING TO THE CART                                                  */
  /* ==================================================================== */

  /* One chain, so two fast clicks cannot land out of order and leave the
   * cart holding the earlier choice. */
  var chain = Promise.resolve();
  var pending = 0;

  function attributesFor() {
    /* Every key is always sent. An empty string DELETES a cart attribute in
     * Shopify, which is what clears the other half — omitting the key would
     * leave a stale collection slot attached to a shipped order. */
    var out = {
      delivery_method: state.method || '',
      delivery_option: state.method === 'delivery' ? state.option || '' : '',
      delivery_date: state.method === 'delivery' && state.option === 'scheduled' ? state.deliveryDate || '' : '',
      delivery_label: state.method === 'delivery' && state.option === 'scheduled' ? state.deliveryLabel || '' : '',
      ibc_pickup_requested: state.method === 'pickup' ? 'true' : '',
      ibc_pickup_date: state.method === 'pickup' ? state.pickupDate || '' : '',
      ibc_pickup_slot_start: state.method === 'pickup' ? state.pickupStart || '' : '',
      ibc_pickup_slot_end: state.method === 'pickup' ? state.pickupEnd || '' : '',
      ibc_pickup_slot_label: state.method === 'pickup' ? state.pickupLabel || '' : '',
      ibc_pickup_location: state.method === 'pickup' ? pickupLocation() : '',
      /* THE PREPARATION TIME THE SLOT WAS CHOSEN UNDER.
       *
       * Not ours to drop. Every collection order the shop has ever taken
       * carries this — #1054 and #1064 through #1083 all have it, reading 60
       * or 180 — because the old scheduler wrote it, and the orders app reads
       * what the orders carry. The first version of this picker computed the
       * number and then failed to write it, which would have left new orders
       * looking different from old ones for no reason anybody could see.
       *
       * It is the basket's own figure: the largest custom.pickup_delay_minutes
       * among the products in it, floored at 60, which is what actually
       * constrained which slots were offered. Worth having on the order as a
       * record of the rule that applied at the time, since the product's
       * metafield can be edited afterwards. */
      ibc_pickup_delay_minutes: state.method === 'pickup' ? String(prepMinutes()) : ''
    };
    return out;
  }

  function pickupLocation() {
    var root = roots()[0];
    return root ? root.getAttribute('data-location') || '' : '';
  }

  function prepMinutes() {
    var root = roots()[0];
    return root ? conf(root).prepMinutes : 60;
  }

  function showError(message) {
    var slot = $one('[data-ibc-cart-error]');
    if (slot) slot.textContent = message;
  }

  function clearError() {
    $all('[data-ibc-cart-error]').forEach(function (slot) {
      slot.textContent = '';
    });
  }

  function commit() {
    var attempt = snapshot();
    pending++;
    setPending(true);
    chain = chain
      .then(function () {
        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes: attributesFor() })
        });
      })
      .then(function (response) {
        if (!response.ok) throw new Error(MSG.failed);
        return response.json();
      })
      .then(function () {
        /* The cart took it. Everything up to and including this write is now
         * the thing to fall back to. */
        committed = attempt;
        clearError();
      })
      .catch(function () {
        /* Put the UI back to the last state the cart actually accepted,
         * rather than leaving a selection on screen that no order will ever
         * carry. */
        restore(committed);
        syncAll();
        showError(MSG.failed);
      })
      .then(function () {
        pending--;
        if (pending <= 0) setPending(false);
      });
    return chain;
  }

  function setPending(on) {
    roots().forEach(function (root) {
      root.classList.toggle('is-saving', !!on);
    });
  }

  /* ==================================================================== */
  /* RENDERING                                                            */
  /* ==================================================================== */

  function monthShown(root, kind) {
    var grid = $one('[data-ibc-cal="' + kind + '"] [data-ibc-cal-grid]', root);
    if (!grid) return null;
    var raw = grid.getAttribute('data-month');
    return raw ? parseISO(raw) : null;
  }

  /* The month a calendar should open on: the chosen date if there is one,
   * otherwise the first date that can actually be chosen — never "today",
   * which on the 30th would open a month with nothing selectable in it. */
  function openMonth(cfg, kind) {
    var chosen = parseISO(kind === 'pickup' ? state.pickupDate : state.deliveryDate);
    var from = chosen || minDate(cfg, kind);
    return new Date(from.getFullYear(), from.getMonth(), 1, 12, 0, 0, 0);
  }

  function renderCalendar(root, kind, monthStart) {
    var wrap = $one('[data-ibc-cal="' + kind + '"]', root);
    if (!wrap) return;
    var grid = $one('[data-ibc-cal-grid]', wrap);
    var title = $one('[data-ibc-cal-month]', wrap);
    if (!grid) return;

    var cfg = conf(root);
    var first = monthStart || openMonth(cfg, kind);
    grid.setAttribute('data-month', toISO(first));
    if (title) title.textContent = MONTHS[first.getMonth()] + ' ' + first.getFullYear();

    var chosenISO = kind === 'pickup' ? state.pickupDate : state.deliveryDate;

    /* Monday-first: JS gives 0 for Sunday, so Sunday is 6 columns in. */
    var lead = (first.getDay() + 6) % 7;
    var daysInMonth = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();

    /* A day cell that currently holds focus is about to be destroyed by the
     * innerHTML below. Remember which one, and hand focus back to the same
     * date afterwards — otherwise selecting a day with Enter drops the
     * keyboard user back at the top of the document. */
    var active = document.activeElement;
    var refocusISO =
      active && grid.contains(active) && active.hasAttribute('data-ibc-cal-day')
        ? active.getAttribute('data-ibc-cal-day')
        : null;

    var html = '';
    var total = Math.ceil((lead + daysInMonth) / 7) * 7;
    var focusISO = null;

    for (var i = 0; i < total; i++) {
      if (i % 7 === 0) html += '<div class="ibc-cal__row" role="row">';

      var dayNum = i - lead + 1;
      if (dayNum < 1 || dayNum > daysInMonth) {
        html += '<div class="ibc-cal__cell ibc-cal__cell--blank" role="gridcell"></div>';
      } else {
        var date = new Date(first.getFullYear(), first.getMonth(), dayNum, 12, 0, 0, 0);
        var iso = toISO(date);
        var verdict = dayState(cfg, kind, date);
        var isChosen = iso === chosenISO;
        var disabled = verdict !== 'ok';
        if (!disabled && !focusISO) focusISO = iso;
        if (isChosen) focusISO = iso;

        html +=
          '<div class="ibc-cal__cell" role="gridcell">' +
          '<button type="button" class="ibc-cal__day' +
          (isChosen ? ' is-chosen' : '') +
          (disabled ? ' is-off' : '') +
          '" data-ibc-cal-day="' + iso + '"' +
          ' data-verdict="' + verdict + '"' +
          (disabled ? ' aria-disabled="true"' : '') +
          ' aria-pressed="' + (isChosen ? 'true' : 'false') + '"' +
          ' tabindex="-1"' +
          '><span class="visually-hidden">' + labelCell(date) +
          (verdict === 'closed' ? ' — closed' : '') +
          (verdict === 'early' ? ' — too soon' : '') +
          (verdict === 'late' ? ' — too far ahead' : '') +
          '</span><span aria-hidden="true">' + dayNum + '</span></button>' +
          '</div>';
      }

      if (i % 7 === 6) html += '</div>';
    }

    grid.innerHTML = html;

    /* Roving tabindex: exactly one day in the grid is reachable by Tab. */
    var target = focusISO ? $one('[data-ibc-cal-day="' + focusISO + '"]', grid) : null;
    if (!target) target = $one('.ibc-cal__day:not([aria-disabled="true"])', grid);
    if (target) target.setAttribute('tabindex', '0');

    if (refocusISO) {
      var back = $one('[data-ibc-cal-day="' + refocusISO + '"]', grid);
      if (back) {
        if (target && target !== back) target.setAttribute('tabindex', '-1');
        back.setAttribute('tabindex', '0');
        back.focus();
      }
    }

    /* Nav buttons: nothing before the first offerable month, nothing after
     * the window closes. A calendar that pages into an empty year is a
     * calendar the customer has to be told to stop using. */
    var prevEnd = new Date(first.getFullYear(), first.getMonth(), 0, 12, 0, 0, 0);
    var nextStart = new Date(first.getFullYear(), first.getMonth() + 1, 1, 12, 0, 0, 0);
    var prev = $one('[data-ibc-cal-nav="-1"]', wrap);
    var next = $one('[data-ibc-cal-nav="1"]', wrap);
    if (prev) prev.disabled = prevEnd < minDate(cfg, kind);
    if (next) next.disabled = nextStart > maxDate(cfg);
  }

  function renderSlots(root) {
    var wrap = $one('[data-ibc-fulfil-slots]', root);
    if (!wrap) return;
    var grid = $one('[data-ibc-fulfil-slot-grid]', wrap);
    var empty = $one('[data-ibc-fulfil-slots-empty]', wrap);
    if (!grid) return;

    var date = parseISO(state.pickupDate);
    wrap.hidden = !date;
    if (!date) {
      grid.innerHTML = '';
      return;
    }

    var cfg = conf(root);
    var slots = slotsFor(cfg, date);
    if (empty) empty.hidden = slots.length > 0;

    grid.innerHTML = slots
      .map(function (slot) {
        var chosen = slot.start === state.pickupStart;
        return (
          '<button type="button" class="ibc-fulfil__slot' + (chosen ? ' is-chosen' : '') + '"' +
          ' data-ibc-fulfil-slot="' + slot.start + '"' +
          ' data-end="' + slot.end + '"' +
          ' data-label="' + slot.label.replace(/"/g, '&quot;') + '"' +
          ' role="radio" aria-checked="' + (chosen ? 'true' : 'false') + '"' +
          ' tabindex="' + (chosen ? '0' : '-1') + '">' +
          /* TWO SPELLINGS OF THE SAME TIME, one shown per layout by CSS.
           *
           * The full range is the accessible name and what the cart page
           * shows. The drawer footer shows the START only, because five
           * columns of "10:00" fit where three columns of "10:00–10:30am"
           * do not, and on a 667px phone that difference is what decides
           * whether the checkout button is on screen. The window length is
           * stated once above the grid instead of seventeen times inside
           * it. */
          '<span class="ibc-fulfil__slot-full">' + slot.time + '</span>' +
          '<span class="ibc-fulfil__slot-short" aria-hidden="true">' + slot.from + '</span>' +
          '</button>'
        );
      })
      .join('');

    if (!$one('.ibc-fulfil__slot[tabindex="0"]', grid)) {
      var firstSlot = $one('.ibc-fulfil__slot', grid);
      if (firstSlot) firstSlot.setAttribute('tabindex', '0');
    }
  }

  /* WHAT HAS BEEN CHOSEN, each line editable.
   *
   * ONE list, rendered once, sitting outside the expanding body so it stays
   * on screen when the panel is put away. It grows as the flow is walked:
   * the method as soon as a tile is tapped, then the speed or the date, then
   * the time.
   *
   * `stage` decides what is SETTLED, not `state`. On the times step a date
   * is settled and a time is not, even though both may be in the cache from
   * an earlier visit — showing a time the customer is in the middle of
   * re-choosing would be describing the past as the present.
   */
  /* THE SETTLED BAR. One line that stands in for the whole picker once the
   * choice is complete and the panel is put away.
   *
   * Shown only when BOTH are true: the basket is ready, and the stage is
   * 'closed'. Ready without closed is somebody mid-flow who has just finished
   * — the review is the right thing to look at, not a summary of it. Closed
   * without ready is a half-made choice, and summarising that would be
   * claiming something was decided when it was not.
   *
   * Returns nothing; it writes into the DOM and lets syncRoot decide what is
   * hidden around it. */
  function renderBar(root) {
    var bar = $one('[data-ibc-fulfil-bar]', root);
    if (!bar) return;

    var slot = $one('[data-ibc-fulfil-bar-text]', bar);
    var on = ready() && stage === 'closed';
    bar.hidden = !on;
    if (!on || !slot) return;

    /* SHORT FORMS THROUGHOUT. This is one line on a 375px phone beside an
     * icon and the word Change, so "Sat 20th Sept" rather than "Saturday 20
     * September". The long form is still one tap away. */
    var method = '';
    var detail = '';

    if (state.method === 'pickup') {
      method = 'Collect';
      var pd = parseISO(state.pickupDate);
      detail = (pd ? labelShort(pd) : '');
      var slotText = state.pickupLabel ? slotTimeOf(state.pickupLabel) : '';
      if (slotText) detail = detail ? detail + ', ' + slotText : slotText;
    } else if (state.method === 'delivery') {
      method = 'Shipping';
      detail = state.option === 'scheduled'
        ? (state.deliveryLabel || '')
        : (root.getAttribute('data-standard-label') || '');
    }

    slot.innerHTML =
      '<span class="ibc-fulfil__bar-method">' + esc(method) + '</span>' +
      (detail ? '<span class="ibc-fulfil__bar-detail">' + esc(detail) + '</span>' : '');

    /* The accessible name is the whole sentence, because the bar is one
     * button and "Change" on its own tells a screen-reader user nothing about
     * what they would be changing. */
    bar.setAttribute(
      'aria-label',
      method + (detail ? ', ' + detail : '') + '. Change.'
    );
  }

  function renderChosen(root) {
    var host = $one('[data-ibc-fulfil-chosen="live"]', root);
    if (!host) return;

    var rows = [];

    /* SHOW EVERYTHING SETTLED, AND HIDE ONLY WHAT IS BEING RE-CHOSEN.
     *
     * The earlier rule was the other way round — a detail appeared only once
     * the flow had reached the review — and it had a bug worth keeping a
     * note about: collapsing the panel moved the stage to 'closed', so the
     * customer's own date vanished from the one thing that was supposed to
     * survive collapsing.
     *
     * A step in progress is the only reason to hold a value back: showing
     * "Date: Sunday 13th" while somebody is mid-way through picking a
     * different day describes the past as the present. Everything else,
     * including when nothing is in progress at all, is shown. */
    function settled(owningStage) {
      return stage !== owningStage;
    }

    /* NO METHOD ROW. "Shipping — By courier — Change" and "Collect — Italian
     * Bear Chocolate — Change" both restated what the lit tile directly above
     * already says, and offered a Change control for a choice the tiles
     * themselves are. Two rows of a footer, spent on nothing.
     *
     * What is left is only what the tiles CANNOT show: the speed, the date,
     * the time. So this list is empty until one of those is settled, which is
     * also why it collapses to nothing on the two steps where the customer
     * has picked a method and no detail yet. */
    if (state.method === 'delivery') {
      if (state.option === 'standard' && settled('options')) {
        rows.push({
          label: 'Speed',
          value: root.getAttribute('data-standard-label') || 'Standard',
          edit: 'options'
        });
      } else if (state.option === 'scheduled' && state.deliveryLabel && settled('calendar')) {
        rows.push({ label: 'Arrives', value: state.deliveryLabel, edit: 'calendar' });
      }
    } else if (state.method === 'pickup') {
      var d = parseISO(state.pickupDate);
      if (d && settled('calendar')) {
        rows.push({ label: 'Date', value: labelLong(d), edit: 'calendar' });
      }
      if (state.pickupLabel && settled('slots')) {
        rows.push({ label: 'Time', value: slotTimeOf(state.pickupLabel), edit: 'slots' });
      }
    }

    host.innerHTML = rows
      .filter(function (row) { return row.value; })
      .map(function (row) {
        return (
          '<div class="ibc-fulfil__row">' +
          '<span class="ibc-fulfil__row-label">' + esc(row.label) + '</span>' +
          '<span class="ibc-fulfil__row-value">' + esc(row.value) + '</span>' +
          '<button type="button" class="ibc-fulfil__row-edit" data-ibc-fulfil-edit="' + row.edit + '">' +
          'Change<span class="visually-hidden"> ' + esc(row.label.toLowerCase()) + '</span>' +
          '</button></div>'
        );
      })
      .join('');
  }

  function esc(value) {
    return String(value === null || value === undefined ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /* 'Sunday 20 September, 6:00-6:30pm' -> '6:00-6:30pm'. The date is already
   * its own row, so repeating it in the time row would be noise. */
  function slotTimeOf(label) {
    var at = String(label || '').lastIndexOf(', ');
    return at === -1 ? String(label || '') : label.slice(at + 2);
  }

  function syncRoot(root) {
    var footer = isFooter(root);

    /* THE SUMMARY STATE. One class, and the stylesheet does the rest: the
     * heading, the tiles and the detail rows all go, and the bar takes their
     * place. Kept as a class rather than six `hidden` attributes so that the
     * whole swap is one rule to read and one rule to undo. */
    if (footer) {
      renderBar(root);
      root.classList.toggle('is-summary', ready() && stage === 'closed');
    }

    /* TABS, and the case where NEITHER is lit.
     *
     * The drawer starts with no method chosen, so `open` can legitimately be
     * the empty string and both panels stay hidden. The roving tabindex then
     * has nobody to rove to, so the FIRST tab holds it — a tab strip that
     * cannot be reached by Tab at all is worse than one whose entry point is
     * arbitrary. */
    var fallback = root.getAttribute('data-preselect') === '0'
      ? ''
      : (root.getAttribute('data-open-tab') || 'delivery');
    var open = state.method || fallback;

    $all('[data-ibc-fulfil-tab]', root).forEach(function (tab, index) {
      var mine = tab.getAttribute('data-ibc-fulfil-tab');
      var on = mine === open;
      tab.setAttribute('aria-selected', on ? 'true' : 'false');
      tab.setAttribute('tabindex', on || (!open && index === 0) ? '0' : '-1');
      var panel = $one('[data-ibc-fulfil-panel="' + mine + '"]', root);
      if (panel) panel.hidden = !on;
    });

    /* Delivery options */
    $all('[data-ibc-fulfil-option]', root).forEach(function (input) {
      var on = state.method === 'delivery' && input.value === state.option;
      input.checked = on;
      /* The card around the radio is highlighted with `:has()` in CSS, which
       * is not everywhere yet. This class is the same rule, stated twice, so
       * the selection is legible in a browser that does not support it. */
      var card = closest(input, '[data-ibc-fulfil-choice]');
      if (card) card.classList.toggle('is-chosen', on);
    });
    /* Calendars — only the visible one is worth drawing, but the hidden one
     * is drawn too so switching tabs never shows an empty month for a frame. */
    renderCalendar(root, 'delivery', monthShown(root, 'delivery'));
    renderCalendar(root, 'pickup', monthShown(root, 'pickup'));
    renderSlots(root);

    /* STAGES. Exactly one block on screen in the footer layout; all of them
     * on the cart page, where `stage` is the sentinel 'all'. */
    if (footer) {
      $all('[data-ibc-fulfil-stage]', root).forEach(function (block) {
        block.hidden = block.getAttribute('data-ibc-fulfil-stage') !== stage;
      });

      renderChosen(root);

      /* The dated-delivery caution belongs to exactly one situation. A
       * standard delivery has no date to hedge about, and a collection slot
       * is a commitment the shop has made rather than a courier's guess — so
       * showing it there would be untrue, not merely noisy. */
      var caution = $one('[data-ibc-fulfil-caution]', root);
      if (caution) {
        caution.hidden = !(state.method === 'delivery' && state.option === 'scheduled');
      }

      var body = $one('[data-ibc-fulfil-body]', root);
      var expanded = stage !== 'closed';
      root.classList.toggle('is-open', expanded);
      if (body) body.setAttribute('aria-hidden', expanded ? 'false' : 'true');

      var collapse = $one('[data-ibc-fulfil-collapse]', root);
      if (collapse) {
        collapse.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        var says = $one('.visually-hidden', collapse);
        if (says) {
          says.textContent = expanded
            ? 'Hide the delivery options'
            : 'Show the delivery options';
        }
      }
    } else {
      /* The cart page shows every STEP at once, but not every branch of a
       * choice already made: the delivery calendar is still revealed by
       * "choose a date" rather than sitting open under a standard-delivery
       * basket. Mirrors f_show_delivery_cal in the snippet. */
      $all('[data-ibc-fulfil-stage]', root).forEach(function (block) {
        block.hidden = false;
      });
      var deliveryCal = $one(
        '[data-ibc-fulfil-panel="delivery"] [data-ibc-fulfil-stage="calendar"]', root);
      if (deliveryCal) {
        deliveryCal.hidden = !(state.method === 'delivery' && state.option === 'scheduled');
      }
      root.classList.add('is-open');
    }

    /* Summary */
    var summary = $one('[data-ibc-fulfil-summary]', root);
    var summaryTextNode = $one('[data-ibc-fulfil-summary-text]', root);
    var text = summaryText(root);
    if (summaryTextNode) summaryTextNode.textContent = ready() ? text : '';
    if (summary) {
      summary.classList.toggle('is-ready', ready());
      /* In the footer it is the one thing that survives collapsing, so it is
       * shown whenever there is something to say AND the flow is not already
       * showing the review. Two statements of the same choice, one above the
       * other, is clutter. */
      /* Footer: never hidden, because it is the live region and the chosen
       * rows carry the visible answer. Panel: shown only when there is one. */
      summary.hidden = footer ? false : !ready();
    }

    root.setAttribute('data-method', state.method);
    root.setAttribute('data-option', state.option);
    root.setAttribute('data-stage', stage);
    root.setAttribute('data-ready', ready() ? '1' : '0');
  }

  /* The checkout button, the close control and the footer's own layout are
   * NOT inside the picker — they are in each section's markup — so they are
   * driven separately and globally.
   *
   * VISIBILITY vs ENABLEMENT, which are different questions here:
   *
   *   hidden    only while the drawer's picker is shut AND nothing has been
   *             chosen. A checkout button under two untouched tiles is an
   *             invitation to press something that cannot work.
   *   disabled  whenever the basket is not ready. Opening the picker reveals
   *             the button immediately, greyed, so the customer can see what
   *             the steps are FOR before they have finished them.
   *
   * A basket that IS ready keeps its button whatever the panel is doing —
   * collapsing must never strand somebody one tap from paying. */
  function syncGate() {
    var open = ready();
    var anyFooter = roots().some(isFooter);
    var expanded = !anyFooter || stage !== 'closed';
    var chosen = !!state.method;

    $all('[data-ibc-fulfil-checkout]').forEach(function (button) {
      button.disabled = !open;
    });

    $all('[data-ibc-fulfil-checkout-wrap]').forEach(function (wrap) {
      wrap.hidden = anyFooter && !expanded && !open;
    });

    /* The close control only exists while there is something to close. */
    $all('[data-ibc-fulfil-collapse]').forEach(function (button) {
      button.hidden = !expanded;
      button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    });

    /* The footer reserves the top-right corner for that control only when it
     * is there, so the subtotal is not permanently indented around nothing. */
    $all('[data-ibc-fulfil-foot]').forEach(function (foot) {
      foot.classList.toggle('is-fulfil-open', expanded);
      foot.classList.toggle('is-fulfil-chosen', chosen);
    });

    $all('[data-ibc-fulfil-gate]').forEach(function (note) {
      note.hidden = open;
      if (!open && !note.textContent.trim()) note.textContent = MSG.gate;
    });
  }

  function syncAll() {
    roots().forEach(syncRoot);
    syncGate();
  }

  /* ==================================================================== */
  /* CHOICES                                                              */
  /* ==================================================================== */

  function chooseMethod(method) {
    /* Re-picking the method you are already on re-opens its step rather than
     * doing nothing. In the drawer that is the only way back out of a
     * collapsed picker by way of the tabs, and doing nothing there reads as
     * a dead button. */
    if (state.method === method) {
      stage = resumeStage();
      syncAll();
      return;
    }

    state.method = method;

    /* Switching method does NOT throw away the other half's choice in the
     * cache, so a customer who flips to collection and back finds their
     * delivery date still there. It IS cleared in the cart — see
     * attributesFor() — because an order must carry one answer, not two.
     *
     * Shipping no longer DEFAULTS to standard, though. In the staged flow the
     * point of the options step is to ask, and answering on the customer's
     * behalf the instant they tap "Shipping" would skip the step and land
     * them on a review of a decision they had not made. */
    stage = nextStage('method');
    syncAll();
    commit();
  }

  function chooseOption(option) {
    state.method = 'delivery';
    state.option = option;
    stage = nextStage('option');
    syncAll();
    commit();
  }

  function chooseDate(root, kind, iso) {
    var date = parseISO(iso);
    if (!date) return;
    var cfg = conf(root);
    if (dayState(cfg, kind, date) !== 'ok') return;

    if (kind === 'pickup') {
      state.method = 'pickup';
      state.pickupDate = iso;
      /* A new day invalidates the slot: 6:30pm on Tuesday is not 6:30pm on
       * Wednesday, and carrying it over would book a time nobody picked. */
      state.pickupStart = '';
      state.pickupEnd = '';
      state.pickupLabel = '';
    } else {
      state.method = 'delivery';
      state.option = 'scheduled';
      state.deliveryDate = iso;
      state.deliveryLabel = labelShort(date);
    }
    stage = nextStage('date');
    syncAll();
    commit();
  }

  function chooseSlot(start, end, label) {
    state.method = 'pickup';
    state.pickupStart = start;
    state.pickupEnd = end;
    state.pickupLabel = label;
    stage = nextStage('slot');
    syncAll();
    commit();
  }

  /* ==================================================================== */
  /* EVENTS — all delegated. See rule 2.                                  */
  /* ==================================================================== */

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;

    /* COLLAPSE. A view, not an undo: the cart attributes are untouched and
     * the summary line stays on screen, so a collapsed picker still says
     * what was chosen and checkout stays live. */
    var collapse = closest(target, '[data-ibc-fulfil-collapse]');
    if (collapse) {
      event.preventDefault();
      setStage(stage === 'closed' ? resumeStage() : 'closed');
      return;
    }

    /* THE SETTLED BAR REOPENS THE PICKER. Deliberately `resumeStage()` rather
     * than 'options': a customer who taps a bar reading "Collect, Sat 20th,
     * 5:00pm" is going back to a finished choice, so the right place to land
     * is the review — with the date and the time there and each editable —
     * and not the method tiles, which would make them walk the whole flow
     * again to change a time. */
    var bar = closest(target, '[data-ibc-fulfil-bar]');
    if (bar) {
      event.preventDefault();
      setStage(resumeStage());
      return;
    }

    /* BACK, and CHANGE from the review. Same mechanism, different word: one
     * walks the flow backwards, the other jumps to the step that owns a
     * particular line. Neither touches the cart. */
    var back = closest(target, '[data-ibc-fulfil-back]');
    if (back) {
      event.preventDefault();
      setStage(back.getAttribute('data-ibc-fulfil-back'));
      return;
    }

    var edit = closest(target, '[data-ibc-fulfil-edit]');
    if (edit) {
      event.preventDefault();
      var to = edit.getAttribute('data-ibc-fulfil-edit');
      /* "Change" on the collapsed summary means "show me the review again",
       * which is the one edit target that is not itself a step. */
      if (to === 'review') {
        setStage('review');
      } else {
        setStage(to);
      }
      return;
    }

    var tab = closest(target, '[data-ibc-fulfil-tab]');
    if (tab) {
      event.preventDefault();
      chooseMethod(tab.getAttribute('data-ibc-fulfil-tab'));
      var root = closest(tab, '[data-ibc-fulfil]');
      var again = root && $one('[data-ibc-fulfil-tab="' + tab.getAttribute('data-ibc-fulfil-tab') + '"]', root);
      if (again) again.focus();
      return;
    }

    var nav = closest(target, '[data-ibc-cal-nav]');
    if (nav) {
      event.preventDefault();
      var navWrap = closest(nav, '[data-ibc-cal]');
      var navRoot = closest(nav, '[data-ibc-fulfil]');
      if (!navWrap || !navRoot) return;
      var kind = navWrap.getAttribute('data-ibc-cal');
      var shown = monthShown(navRoot, kind) || openMonth(conf(navRoot), kind);
      var step = Number(nav.getAttribute('data-ibc-cal-nav')) || 0;
      var moved = new Date(shown.getFullYear(), shown.getMonth() + step, 1, 12, 0, 0, 0);
      /* Both surfaces page together, so the drawer and the page never show
       * different months of the same calendar. */
      roots().forEach(function (each) {
        renderCalendar(each, kind, moved);
      });
      return;
    }

    var day = closest(target, '[data-ibc-cal-day]');
    if (day && day.getAttribute('aria-disabled') !== 'true') {
      event.preventDefault();
      var dayWrap = closest(day, '[data-ibc-cal]');
      var dayRoot = closest(day, '[data-ibc-fulfil]');
      if (!dayWrap || !dayRoot) return;
      chooseDate(dayRoot, dayWrap.getAttribute('data-ibc-cal'), day.getAttribute('data-ibc-cal-day'));
      return;
    }

    var slot = closest(target, '[data-ibc-fulfil-slot]');
    if (slot) {
      event.preventDefault();
      chooseSlot(
        slot.getAttribute('data-ibc-fulfil-slot'),
        slot.getAttribute('data-end'),
        slot.getAttribute('data-label')
      );
      return;
    }

    var why = closest(target, '[data-ibc-fulfil-why]');
    if (why) {
      event.preventDefault();
      var whyRoot = closest(why, '[data-ibc-fulfil]');
      var body = whyRoot && $one('[data-ibc-fulfil-why-body]', whyRoot);
      if (!body) return;
      var opened = why.getAttribute('aria-expanded') === 'true';
      why.setAttribute('aria-expanded', opened ? 'false' : 'true');
      body.hidden = opened;
    }
  });

  document.addEventListener('change', function (event) {
    var input = event.target;
    if (!input || !input.hasAttribute || !input.hasAttribute('data-ibc-fulfil-option')) return;
    chooseOption(input.value);
  });

  /* Keyboard: the tab strip, and the grid. Both follow the usual pattern —
   * arrows move, and moving a roving focus is not the same as choosing. */
  document.addEventListener('keydown', function (event) {
    var key = event.key;

    var tab = closest(event.target, '[data-ibc-fulfil-tab]');
    if (tab && (key === 'ArrowLeft' || key === 'ArrowRight' || key === 'Home' || key === 'End')) {
      var strip = closest(tab, '.ibc-fulfil__tabs');
      if (!strip) return;
      var tabs = $all('[data-ibc-fulfil-tab]', strip);
      var at = tabs.indexOf(tab);
      var to = at;
      if (key === 'ArrowLeft') to = (at - 1 + tabs.length) % tabs.length;
      if (key === 'ArrowRight') to = (at + 1) % tabs.length;
      if (key === 'Home') to = 0;
      if (key === 'End') to = tabs.length - 1;
      event.preventDefault();
      tabs[to].focus();
      chooseMethod(tabs[to].getAttribute('data-ibc-fulfil-tab'));
      return;
    }

    var day = closest(event.target, '[data-ibc-cal-day]');
    if (!day) return;

    var from = parseISO(day.getAttribute('data-ibc-cal-day'));
    if (!from) return;
    var column = (from.getDay() + 6) % 7;

    var step = 0;
    if (key === 'ArrowLeft') step = -1;
    else if (key === 'ArrowRight') step = 1;
    else if (key === 'ArrowUp') step = -7;
    else if (key === 'ArrowDown') step = 7;
    else if (key === 'Home') step = -column;
    else if (key === 'End') step = 6 - column;
    else if (key === 'PageUp') step = -28;
    else if (key === 'PageDown') step = 28;
    else return;

    event.preventDefault();

    var wrap = closest(day, '[data-ibc-cal]');
    var root = closest(day, '[data-ibc-fulfil]');
    if (!wrap || !root) return;
    var kind = wrap.getAttribute('data-ibc-cal');
    var to = addDays(from, step);
    var cfg = conf(root);

    /* Do not walk out of the offerable range: clamp instead, so holding an
     * arrow key cannot strand focus in 2031. */
    if (to < minDate(cfg, kind)) to = minDate(cfg, kind);
    if (to > maxDate(cfg)) to = maxDate(cfg);

    var targetISO = toISO(to);
    var monthStart = new Date(to.getFullYear(), to.getMonth(), 1, 12, 0, 0, 0);
    var shown = monthShown(root, kind);
    if (!shown || shown.getMonth() !== monthStart.getMonth() || shown.getFullYear() !== monthStart.getFullYear()) {
      roots().forEach(function (each) {
        renderCalendar(each, kind, monthStart);
      });
    }

    var next = $one('[data-ibc-cal="' + kind + '"] [data-ibc-cal-day="' + targetISO + '"]', root);
    if (next) {
      $all('[data-ibc-cal="' + kind + '"] [data-ibc-cal-day]', root).forEach(function (each) {
        each.setAttribute('tabindex', '-1');
      });
      next.setAttribute('tabindex', '0');
      next.focus();
    }
  });

  /* ==================================================================== */
  /* SURVIVING THE SECTION SWAP                                           */
  /* ==================================================================== */

  /* A SCOPED observer, not one over document.body.
   *
   * `assets/ibc-cart.js` re-renders by assigning innerHTML on the two
   * `[data-ibc-cart-refresh]` hosts. Those hosts are never themselves
   * replaced, so one childList observer per host — two nodes on /cart, one
   * everywhere else — sees every swap and nothing else on the page.
   *
   * The event alternative does not work in both directions: `ibc:cart-changed`
   * fires AFTER the swap when the cart itself mutated, but BEFORE it when the
   * product page raised it, because ibc-cart.js starts its own async refresh
   * from that same listener. Watching the node is true in both cases. */
  var observer = null;

  function watch() {
    if (typeof MutationObserver !== 'function') return;
    if (observer) observer.disconnect();
    observer = new MutationObserver(function () {
      seed();
      syncAll();
    });
    $all('[data-ibc-cart-refresh]').forEach(function (host) {
      observer.observe(host, { childList: true });
    });
  }

  function start() {
    if (!roots().length) {
      /* Nothing to do on this page, but the basket may become non-empty via
       * the drawer, so the hosts are still watched. */
      watch();
      return;
    }
    seed();
    syncAll();
    watch();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  window.IbcFulfilment = {
    sync: syncAll,
    stage: function () { return stage; },
    setStage: setStage,
    state: function () {
      var copy = {};
      Object.keys(state).forEach(function (key) { copy[key] = state[key]; });
      return copy;
    },
    ready: ready,
    attributes: attributesFor,
    slotsFor: slotsFor,
    dayState: dayState,
    /* Exposed for the harness, which has to be able to ask what the earliest
     * offered date is on a given day without waiting for that day. */
    minDate: minDate,
    conf: conf
  };
})();
